import React, { useEffect, useRef, useState } from "react";
import "./TinkTracker.css";

// 5d: Tink Tracker — radar overlay for the HEA HLS sync screen. A gold
// "news graphic" radar (rings, sweep beam, pulses) with a live countdown
// to the moment Tinkerbell flies off the castle (17:21 into Happily Ever
// After). Pixie particles trail the sweep continuously; at the target
// moment a 3-cycle burst flyoff fires, then the radar slides off-right.
//
// Ported from multitest2 (tink* functions line 4726-5000, .tink-radar
// CSS line 1481-1800). Reimplemented React-idiomatically:
//   - imperative DOM getElementById/classList → component state + refs
//   - showPos arrives via a ref the parent updates each cue tick; this
//     component polls it on its own cadence (no parent re-render churn)
//
// Props:
//   enabled     — tinkUserEnabled (user toggle). When false the radar
//                 slides off-screen-right but the session keeps running.
//   isHea       — true when the synced show is happily-ever-after.
//   showPosRef  — ref holding latest cue-runner showPos (seconds) or null.

const TINK_TARGET_SEC = 1041; // Tinkerbell flies off at 17:21
const TINK_BURST_WINDOW_SEC = 3; // allow burst trigger within this window past target
const TINK_BURST_DEBOUNCE_MS = 5000; // don't re-fire burst within 5s
const TINK_POLL_MS = 200; // how often we read showPosRef
const PIXIE_SPAWN_MS = 160; // continuous pixie cadence (~6/sec)

function fmtTime(sec) {
  if (sec < 0) sec = 0;
  const m = Math.floor(sec / 60);
  const s = Math.floor(sec % 60);
  return `${m}:${String(s).padStart(2, "0")}`;
}

function TinkTracker({ enabled, isHea, showPosRef }) {
  // phase: "idle" (not HEA / no showPos yet) → "active" (radar visible,
  // counting down) → "fired" (3-cycle burst running) → "exiting" (slide
  // off-right) → "gone" (torn down).
  const [phase, setPhase] = useState("idle");
  const [countdown, setCountdown] = useState(fmtTime(TINK_TARGET_SEC));
  const [urgent, setUrgent] = useState(false);
  const [firedFlash, setFiredFlash] = useState(false);

  const phaseRef = useRef("idle");
  useEffect(() => {
    phaseRef.current = phase;
  }, [phase]);

  const pixieLayerRef = useRef(null);
  const lastFireMsRef = useRef(0);
  const spawnIntervalRef = useRef(null);
  const pollIntervalRef = useRef(null);
  const flyoffTimersRef = useRef([]);

  // ---- pixie spawning ----
  const spawnPixie = (burst = false) => {
    const layer = pixieLayerRef.current;
    if (!layer) return;
    const p = document.createElement("div");
    p.className = "pixie";
    // Leading edge of the 3.5s sweep, in degrees.
    const sweepAngle = ((Date.now() % 3500) / 3500) * 360;
    let angle, dist;
    if (burst) {
      angle = Math.random() * 360;
      dist = 30 + Math.random() * 70;
    } else {
      angle = sweepAngle - 20 + (Math.random() * 40 - 20);
      dist = 20 + Math.random() * 70;
    }
    const rad = ((angle - 90) * Math.PI) / 180;
    const x = 50 + (Math.cos(rad) * dist) / 2;
    const y = 50 + (Math.sin(rad) * dist) / 2;
    p.style.left = `calc(${x}% - 2px)`;
    p.style.top = `calc(${y}% - 2px)`;
    // Color variation — mostly cream/gold, occasional white-hot or green.
    const tint = Math.random();
    if (tint < 0.2) {
      p.style.background =
        "radial-gradient(circle, #d4ffce 0%, #a8e8a0 50%, transparent 70%)";
    } else if (tint < 0.4) {
      p.style.background =
        "radial-gradient(circle, #ffffff 0%, #fff5d0 40%, transparent 70%)";
    }
    layer.appendChild(p);
    setTimeout(() => {
      if (p.parentNode) p.parentNode.removeChild(p);
    }, 1700);
  };

  // ---- 3-cycle burst flyoff, then slide off-right ----
  const fireFlyoff = () => {
    const cycleMs = 1500;
    // toggle the .fired flash class for the burst-star + megapulse
    for (let cycle = 0; cycle < 3; cycle++) {
      const cycleStart = cycle * cycleMs;
      const t = setTimeout(() => {
        // restart the flash keyframes by toggling off→on
        setFiredFlash(false);
        // a microtask later, turn it back on (forces re-fire)
        requestAnimationFrame(() => setFiredFlash(true));
        for (let i = 0; i < 40; i++) {
          const pt = setTimeout(() => spawnPixie(true), i * 25);
          flyoffTimersRef.current.push(pt);
        }
      }, cycleStart);
      flyoffTimersRef.current.push(t);
    }
    setPhase("fired");
    // After 3 cycles + 600ms settle → slide off-right.
    const settleAfter = cycleMs * 3 + 600;
    const exitT = setTimeout(() => {
      setFiredFlash(false);
      setPhase("exiting");
      const goneT = setTimeout(() => {
        setPhase("gone");
        if (spawnIntervalRef.current) {
          clearInterval(spawnIntervalRef.current);
          spawnIntervalRef.current = null;
        }
      }, 950); // matches the 0.9s slide-out animation
      flyoffTimersRef.current.push(goneT);
    }, settleAfter);
    flyoffTimersRef.current.push(exitT);
  };

  // ---- main poll loop: reads showPos, drives countdown + flyoff ----
  useEffect(() => {
    // Reset everything when (re)mounting or HEA status changes.
    setPhase("idle");
    setCountdown(fmtTime(TINK_TARGET_SEC));
    setUrgent(false);
    setFiredFlash(false);
    lastFireMsRef.current = 0;

    if (!isHea) return; // radar only exists for Happily Ever After

    // 5d: dev helper — force the radar near (or past) the flyoff target so
    // you don't have to wait 17 minutes. beaconTestTink(secondsBefore=3)
    // overrides the effective showPos for ~secondsBefore seconds, then lets
    // it cross zero → burst flyoff fires. beaconTestTink(0) fires now.
    const testOverrideRef = { sec: null, until: 0 };
    window.beaconTestTink = (secondsBefore = 3) => {
      const s = Number(secondsBefore);
      const start = TINK_TARGET_SEC - (Number.isFinite(s) ? s : 3);
      testOverrideRef.sec = start;
      testOverrideRef.until = Date.now() + (s + 4) * 1000;
      console.log(
        `🧚 [tink test] forcing showPos to ${start}s ` +
          `(${Number.isFinite(s) ? s : 3}s before flyoff at ${TINK_TARGET_SEC}s)`
      );
    };

    const poll = () => {
      let showPos = showPosRef.current;
      // Test override: synthesize an advancing showPos toward the target.
      if (testOverrideRef.sec != null && Date.now() < testOverrideRef.until) {
        testOverrideRef.sec += TINK_POLL_MS / 1000;
        showPos = testOverrideRef.sec;
      } else if (testOverrideRef.sec != null) {
        testOverrideRef.sec = null; // expired
      }
      if (showPos == null) return; // no valid showPos yet → stay idle (armed)

      const ph = phaseRef.current;

      // Promote idle → active the moment a real showPos arrives.
      if (ph === "idle") {
        setPhase("active");
        // start the continuous pixie spawner
        if (!spawnIntervalRef.current) {
          spawnIntervalRef.current = setInterval(() => {
            const p = phaseRef.current;
            if (p === "gone") return;
            if (p === "exiting") return;
            spawnPixie(false);
          }, PIXIE_SPAWN_MS);
        }
      }

      if (ph === "exiting" || ph === "gone") return;

      const remain = TINK_TARGET_SEC - showPos;
      setCountdown(fmtTime(Math.max(0, remain)));
      setUrgent(remain > 0 && remain <= 10);

      // Fire the burst when crossing zero (with a small past-target window
      // + debounce so showPos jitter around 0 doesn't double-fire).
      if (
        remain <= 0 &&
        remain > -TINK_BURST_WINDOW_SEC &&
        Date.now() - lastFireMsRef.current > TINK_BURST_DEBOUNCE_MS &&
        ph === "active"
      ) {
        lastFireMsRef.current = Date.now();
        fireFlyoff();
      }
    };

    poll();
    pollIntervalRef.current = setInterval(poll, TINK_POLL_MS);

    return () => {
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
        pollIntervalRef.current = null;
      }
      if (spawnIntervalRef.current) {
        clearInterval(spawnIntervalRef.current);
        spawnIntervalRef.current = null;
      }
      flyoffTimersRef.current.forEach((t) => clearTimeout(t));
      flyoffTimersRef.current = [];
      delete window.beaconTestTink;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isHea]);

  // Don't render at all outside an active HEA session.
  if (!isHea) return null;
  if (phase === "idle" || phase === "gone") return null;

  const radarClass =
    "tink-radar tink-active" +
    (firedFlash ? " fired" : "") +
    (phase === "exiting" ? " tink-exiting" : "") +
    // user-toggle slide: when disabled, slide off-right via transition
    (!enabled ? " tink-slide-anim tink-slide-off" : " tink-slide-anim");

  return (
    <div className={radarClass}>
      <div className="tink-disc">
        <div className="tink-ring r1"></div>
        <div className="tink-ring r2"></div>
        <div className="tink-ring r3"></div>
        <div className="tink-axis h"></div>
        <div className="tink-axis v"></div>
        <div className="tink-sweep"></div>
        <div className="tink-sweep-line"></div>
        <div className="tink-pulse p1"></div>
        <div className="tink-pulse p2"></div>
        <div className="tink-pulse p3"></div>
        <div className="tink-pixie-layer" ref={pixieLayerRef}></div>
      </div>
      <div className="tink-label-top">
        <span className="tt-1">TINK</span>
        <span className="tt-2">TRACKER</span>
      </div>
      <div className={`tink-countdown${urgent ? " urgent" : ""}`}>
        {countdown}
      </div>
      <div className="tink-burst">
        <div className="tink-burst-star"></div>
      </div>
      <div className="tink-megapulse"></div>
    </div>
  );
}

export default TinkTracker;
